/* ==========================================================================
   Virsaay — The Virsaay Edit Controller
   Handles masked editorial text reveals, staggered clip-path image reveals,
   and GSAP pinned push-up parallax animation.
   ========================================================================== */

(function () {
  'use strict';

  function waitForMotion(callback) {
    if (window.VirsaayMotion && window.VirsaayMotion.ready) {
      window.VirsaayMotion.ready(callback);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.VirsaayMotion && window.VirsaayMotion.ready) {
        clearInterval(timer);
        window.VirsaayMotion.ready(callback);
      } else if (attempts > 60) {
        clearInterval(timer);
        if (window.gsap && window.ScrollTrigger) {
          callback(window.gsap, window.ScrollTrigger);
        }
      }
    }, 50);
  }

  function initVirsaayEdit(section) {
    if (!section || section.dataset.tveInitialized === 'true') return;
    section.dataset.tveInitialized = 'true';

    var eyebrow = section.querySelector('[data-tve-eyebrow]');
    var headlineLines = section.querySelectorAll('.tve-line');
    var bodyText = section.querySelector('[data-tve-body]');
    var linkBtn = section.querySelector('[data-tve-link]');
    var imageBoxes = section.querySelectorAll('[data-tve-img-box]');
    var innerImages = section.querySelectorAll('.tve-img');

    if (!headlineLines.length && !imageBoxes.length) return;

    waitForMotion(function (gsap, ScrollTrigger) {
      if (!gsap || !ScrollTrigger) return;

      if (window.VirsaayMotion && window.VirsaayMotion.reduced) {
        gsap.set([eyebrow, headlineLines, bodyText, linkBtn], { opacity: 1, y: 0 });
        gsap.set(imageBoxes, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 });
        return;
      }

      gsap.registerPlugin(ScrollTrigger);

      var ctx = gsap.context(function () {
        var isMobile = window.innerWidth < 990;

        // Set initial states
        if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 16 });
        gsap.set(headlineLines, { opacity: 0, y: 40 });
        if (bodyText) gsap.set(bodyText, { opacity: 0, y: 24 });
        if (linkBtn) gsap.set(linkBtn, { opacity: 0, y: 16 });

        // Initial image reveal state: clipped inset + slight opacity
        gsap.set(imageBoxes, {
          clipPath: 'inset(8% 0% 8% 0%)',
          opacity: 0.8
        });

        // ------------------------------------------------------------------
        // Main Reveal Timeline (Triggers when section enters viewport)
        // ------------------------------------------------------------------
        var revealTl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        });

        // 1. Eyebrow fades in
        if (eyebrow) {
          revealTl.to(eyebrow, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out'
          }, 0);
        }

        // 2. Headline reveals line-by-line (masked)
        revealTl.to(headlineLines, {
          opacity: 1,
          y: 0,
          duration: 0.95,
          ease: 'power3.out',
          stagger: 0.12
        }, 0.1);

        // 3. Supporting paragraph fades upward after headline
        if (bodyText) {
          revealTl.to(bodyText, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out'
          }, 0.45);
        }

        // 4. CTA link reveals last
        if (linkBtn) {
          revealTl.to(linkBtn, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out'
          }, 0.6);
        }

        // 5. Image independent clipped reveals
        revealTl.to(imageBoxes, {
          clipPath: 'inset(0% 0% 0% 0%)',
          opacity: 1,
          duration: 1.1,
          ease: 'power3.inOut',
          stagger: {
            each: 0.14,
            from: 'start'
          }
        }, 0.25);

        // ------------------------------------------------------------------
        // Pinned Push-Up & Internal Parallax Effect on Scroll
        // ------------------------------------------------------------------
        if (!isMobile) {
          // Subtle internal parallax: image moves inside container
          innerImages.forEach(function (img, idx) {
            var speeds = [-5, 6, -4, 5];
            var delta = speeds[idx % speeds.length];
            gsap.fromTo(img, {
              yPercent: -delta
            }, {
              yPercent: delta,
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
              }
            });
          });

          // Pinned Push-Up: Images stagger-lift upward on scroll
          var pushTl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top 15%',
              end: '+=80%',
              scrub: 0.7,
              pin: false
            }
          });

          // Staggered push up deltas for multi-plane depth
          var boxArr = Array.prototype.slice.call(imageBoxes);
          if (boxArr[0]) pushTl.to(boxArr[0], { y: -24, ease: 'none' }, 0);
          if (boxArr[1]) pushTl.to(boxArr[1], { y: -38, ease: 'none' }, 0);
          if (boxArr[2]) pushTl.to(boxArr[2], { y: -18, ease: 'none' }, 0);
          if (boxArr[3]) pushTl.to(boxArr[3], { y: -32, ease: 'none' }, 0);
        }

      }, section);

      // Refresh triggers once loader completes and reveals layout
      if (window.VirsaayMotion && window.VirsaayMotion.on) {
        window.VirsaayMotion.on('loader:done', function () {
          ScrollTrigger.refresh();
        });
      }

      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      }, { once: true });

      if (window.VirsaayMotion && window.VirsaayMotion.register) {
        var st = ScrollTrigger.getAll().pop();
        if (st) window.VirsaayMotion.register(section, st);
      }

      section._tveCtx = ctx;
    });
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-virsaay-edit]');
    for (var i = 0; i < sections.length; i++) {
      initVirsaayEdit(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Section events
  document.addEventListener('shopify:section:load', function (evt) {
    var el = evt.target.querySelector('[data-virsaay-edit]') || evt.target;
    if (el && el.hasAttribute('data-virsaay-edit')) {
      el.dataset.tveInitialized = '';
      initVirsaayEdit(el);
    }
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var el = evt.target.querySelector('[data-virsaay-edit]') || evt.target;
    if (el && el._tveCtx) el._tveCtx.revert();
  });
})();
